import { Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '../services/toast.service';
import { DashboardSalvar, DashboardWidget } from '../types/Dashboard.type';
import { WIDGET_REGISTRY } from '../widgets/core/widget-registry';
import { WidgetEscolhido } from './adicionar-widget/adicionar-widget.component';
import { DashboardApiService } from './dashboard-api.service';
import { COLUNAS, ItemLocal, Posicao, cabe, ocupacao, ordemMobile, slotsVazios, totalLinhas } from './dashboard-layout';

/** Breakpoint do celular (o mesmo do layout do projeto): somente visualização, 1 coluna. */
const MEDIA_MOBILE = '(max-width: 991px)';
const DEBOUNCE_CONFIG_MS = 800;
/** Distância da borda da janela (px) que ativa a rolagem automática durante o arraste. */
const BORDA_ROLAGEM = 60;
const VELOCIDADE_ROLAGEM = 12;

interface Arraste {
  item: ItemLocal;
  /** Célula do widget em que o usuário pegou (relativa ao canto superior esquerdo). */
  deslocamento: Posicao;
  destino: Posicao | null;
  valido: boolean;
  x: number;
  y: number;
}

/** Dashboard de widgets do usuário (FRONTEND.md §3). */
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit, OnDestroy {

  @ViewChild('grade') grade?: ElementRef<HTMLElement>;

  itens: ItemLocal[] = [];
  removidos: string[] = [];
  modoEdicao = false;
  mobile = false;

  carregando = true;
  erroCarregamento = false;
  salvando = false;
  /** Erros do HTTP 422 ao salvar o layout. */
  erros: string[] = [];
  /** 422 no salvamento automático do config: o layout mudou em outra aba. */
  alteradoEmOutroLugar = false;

  modalVisivel = false;
  slotModal: Posicao | null = null;
  arraste: Arraste | null = null;

  private snapshot: ItemLocal[] | null = null;
  private sequencia = 0;
  private debounceConfig?: ReturnType<typeof setTimeout>;
  private rolagem?: ReturnType<typeof setInterval>;
  private readonly midia = window.matchMedia(MEDIA_MOBILE);
  private readonly aoMudarMidia = (e: MediaQueryListEvent) => this.zone.run(() => this.mobile = e.matches);

  constructor(private dashboardApi: DashboardApiService, private toastService: ToastService, private zone: NgZone) { }

  ngOnInit(): void {
    this.mobile = this.midia.matches;
    this.midia.addEventListener('change', this.aoMudarMidia);
    this.carregar();
  }

  ngOnDestroy(): void {
    this.midia.removeEventListener('change', this.aoMudarMidia);
    this.pararRolagem();
    this.descarregarConfigPendente();
  }

  /** Edição só existe no computador. */
  get edicaoAtiva(): boolean {
    return this.modoEdicao && !this.mobile;
  }

  /** No celular: 1 coluna, ordem por linha e coluna, sem widgets indisponíveis. */
  get itensExibidos(): ItemLocal[] {
    return this.mobile ? ordemMobile(this.itens.filter(i => !i.semComponente)) : this.itens;
  }

  get linhas(): number {
    return totalLinhas(this.itens, this.edicaoAtiva);
  }

  get slots(): Posicao[] {
    return this.edicaoAtiva ? slotsVazios(this.itens, this.linhas) : [];
  }

  get linhasDaGrade(): string | null {
    return !this.mobile && this.linhas ? `repeat(${this.linhas}, var(--altura-linha))` : null;
  }

  carregar() {
    this.carregando = true;
    this.erroCarregamento = false;

    this.dashboardApi.carregar().subscribe({
      next: (widgets) => {
        this.itens = widgets.map(w => this.paraItemLocal(w, w.id));
        this.removidos = [];
        this.erros = [];
        this.alteradoEmOutroLugar = false;
        this.modoEdicao = false;
        this.snapshot = null;
        this.carregando = false;
      },
      error: (error: HttpErrorResponse) => {
        this.carregando = false;
        this.erroCarregamento = true;
        console.error(error);
      }
    });
  }

  editar() {
    this.descarregarConfigPendente();
    this.snapshot = this.itens.map(i => ({ ...i }));
    this.removidos = [];
    this.erros = [];
    this.modoEdicao = true;
  }

  cancelar() {
    if (this.snapshot) this.itens = this.snapshot;
    this.snapshot = null;
    this.removidos = [];
    this.erros = [];
    this.modoEdicao = false;
  }

  /** Abre o modal: pelo + de um slot (posição definida) ou pelo botão do topo (primeira posição livre). */
  iniciarAdicao(slot: Posicao | null) {
    if (!this.modoEdicao) this.editar();
    this.slotModal = slot;
    this.modalVisivel = true;
  }

  adicionar({ widget, posicao }: WidgetEscolhido) {
    this.itens = [...this.itens, {
      chave: `novo-${++this.sequencia}`,
      widgetId: widget.id,
      identificador: widget.identificador,
      nome: widget.nome,
      icone: widget.icone,
      largura: widget.largura,
      altura: widget.altura,
      linha: posicao.linha,
      coluna: posicao.coluna,
      config: null,
      semComponente: !WIDGET_REGISTRY[widget.identificador],
    }];
  }

  /** Remove sem mover os demais widgets (buracos são permitidos). */
  remover(item: ItemLocal) {
    this.itens = this.itens.filter(i => i !== item);
    if (item.id) this.removidos = [...this.removidos, item.id];
  }

  salvar() {
    this.salvando = true;
    this.erros = [];

    this.dashboardApi.salvar(this.montarPayload()).subscribe({
      next: (widgets) => {
        this.salvando = false;
        this.aplicarRetorno(widgets);
        this.modoEdicao = false;
        this.snapshot = null;
        this.toastService.sucesso('Dashboard', 'Dashboard salvo com sucesso.');
      },
      error: (error: HttpErrorResponse) => {
        this.salvando = false;
        if (error.status === 422) {
          this.erros = error.error?.erros?.length ? error.error.erros : ['O layout não pôde ser salvo.'];
          return;
        }
        this.toastService.erro('Erro', typeof error.error === 'string' && error.error ? error.error : 'Erro ao salvar o dashboard');
        console.error(error);
      }
    });
  }

  /** `configChange` de um widget (FRONTEND.md §3.9). */
  aoAlterarConfig(item: ItemLocal, config: Record<string, unknown>) {
    item.config = config;
    if (this.modoEdicao) return;   // vai junto com o "Salvar"

    clearTimeout(this.debounceConfig);
    this.debounceConfig = setTimeout(() => this.salvarConfig(), DEBOUNCE_CONFIG_MS);
  }

  // ---------------------------------------------------------------- arrastar e soltar (FRONTEND.md §3.5)

  iniciarArraste(item: ItemLocal, evento: PointerEvent) {
    if (!this.edicaoAtiva || evento.button !== 0) return;
    evento.preventDefault();

    const celula = this.celulaNoPonto(evento.clientX, evento.clientY);
    if (!celula) return;

    // texto selecionado faz o navegador iniciar um arrastar nativo e interromper os eventos de ponteiro
    window.getSelection()?.removeAllRanges();
    (evento.target as Element).setPointerCapture?.(evento.pointerId);

    this.arraste = {
      item,
      deslocamento: {
        linha: Math.min(Math.max(0, celula.linha - item.linha), item.altura - 1),
        coluna: Math.min(Math.max(0, celula.coluna - item.coluna), item.largura - 1),
      },
      destino: null,
      valido: false,
      x: evento.clientX,
      y: evento.clientY,
    };
    this.moverArraste(evento);
  }

  @HostListener('document:pointermove', ['$event'])
  moverArraste(evento: PointerEvent) {
    const arraste = this.arraste;
    if (!arraste) return;

    arraste.x = evento.clientX;
    arraste.y = evento.clientY;
    this.atualizarDestino();
    this.ajustarRolagem();
  }

  private atualizarDestino() {
    const arraste = this.arraste;
    if (!arraste) return;

    const celula = this.celulaNoPonto(arraste.x, arraste.y);
    if (!celula) {
      arraste.destino = null;
      arraste.valido = false;
      return;
    }

    const { item, deslocamento } = arraste;
    const linha = celula.linha - deslocamento.linha;
    const coluna = celula.coluna - deslocamento.coluna;
    arraste.valido = cabe(linha, coluna, item.largura, item.altura, ocupacao(this.itens, item));
    // destino inválido é mostrado (em vermelho) dentro dos limites da grade
    arraste.destino = arraste.valido
      ? { linha, coluna }
      : { linha: Math.max(0, linha), coluna: Math.max(0, Math.min(coluna, COLUNAS - item.largura)) };
  }

  @HostListener('document:pointerup')
  soltarArraste() {
    const arraste = this.arraste;
    if (!arraste) return;

    // posição inválida: o item volta à posição original
    if (arraste.valido && arraste.destino) {
      this.itens = this.itens.map(i => i === arraste.item ? { ...i, ...arraste.destino } : i);
    }
    this.arraste = null;
    this.pararRolagem();
  }

  @HostListener('document:pointercancel')
  cancelarArraste() {
    this.arraste = null;
    this.pararRolagem();
  }

  @HostListener('document:dragstart', ['$event'])
  impedirArrasteNativo(evento: DragEvent) {
    if (this.arraste) evento.preventDefault();
  }

  // ---------------------------------------------------------------- privados

  /** Rola a página enquanto o ponteiro estiver perto da borda superior/inferior durante o arraste. */
  private ajustarRolagem() {
    const y = this.arraste?.y ?? 0;
    const direcao = y < BORDA_ROLAGEM ? -1 : y > window.innerHeight - BORDA_ROLAGEM ? 1 : 0;

    if (!direcao) {
      this.pararRolagem();
      return;
    }
    if (this.rolagem) return;

    this.zone.runOutsideAngular(() => {
      this.rolagem = setInterval(() => {
        const atual = this.arraste?.y ?? 0;
        const sentido = atual < BORDA_ROLAGEM ? -1 : atual > window.innerHeight - BORDA_ROLAGEM ? 1 : 0;
        if (!this.arraste || !sentido) {
          this.zone.run(() => this.pararRolagem());
          return;
        }
        window.scrollBy(0, sentido * VELOCIDADE_ROLAGEM);
        this.zone.run(() => this.atualizarDestino());
      }, 16);
    });
  }

  private pararRolagem() {
    clearInterval(this.rolagem);
    this.rolagem = undefined;
  }

  private montarPayload(): DashboardSalvar {
    return {
      // todos os itens, inclusive os sem componente, na ordem de `itens` (o 422 numera por essa ordem)
      widgets: this.itens.map(i => i.id
        ? { id: i.id, linha: i.linha, coluna: i.coluna, config: i.config }
        : { widgetId: i.widgetId, linha: i.linha, coluna: i.coluna, config: i.config }),
      removidos: this.removidos,
    };
  }

  /** Substitui o estado pelo retorno do backend, mantendo as chaves para não recriar os widgets. */
  private aplicarRetorno(widgets: DashboardWidget[]) {
    const novos = this.itens.filter(i => !i.id);

    this.itens = widgets.map(w => {
      const existente = this.itens.find(i => i.id === w.id);
      if (existente) return this.paraItemLocal(w, existente.chave, existente.config);

      const adicionado = novos.find(i => i.widgetId === w.widgetId && i.linha === w.linha && i.coluna === w.coluna);
      if (adicionado) novos.splice(novos.indexOf(adicionado), 1);
      return this.paraItemLocal(w, adicionado?.chave ?? w.id, adicionado?.config);
    });
    this.removidos = [];
    this.erros = [];
  }

  private salvarConfig() {
    this.debounceConfig = undefined;

    this.dashboardApi.salvar(this.montarPayload()).subscribe({
      next: (widgets) => {
        // se o usuário entrou no modo edição enquanto salvava, não sobrescreve o estado local
        if (!this.modoEdicao) this.aplicarRetorno(widgets);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 422) {
          this.alteradoEmOutroLugar = true;
          return;
        }
        this.toastService.erro('Erro', 'Erro ao salvar a configuração do widget');
        console.error(error);
      }
    });
  }

  /** Dispara imediatamente o salvamento de config pendente do debounce. */
  private descarregarConfigPendente() {
    if (this.debounceConfig === undefined) return;
    clearTimeout(this.debounceConfig);
    this.salvarConfig();
  }

  /**
   * Mantém a referência do config local quando o backend devolve um objeto igual,
   * para o host não reenviar o mesmo config ao widget.
   */
  private paraItemLocal(w: DashboardWidget, chave: string, configLocal?: Record<string, unknown> | null): ItemLocal {
    const config = configLocal !== undefined && JSON.stringify(configLocal) === JSON.stringify(w.config) ? configLocal : w.config;
    return { ...w, config, chave, semComponente: !WIDGET_REGISTRY[w.identificador] };
  }

  /** Célula da grade sob o ponteiro, a partir do tamanho real das trilhas. */
  private celulaNoPonto(x: number, y: number): Posicao | null {
    const grade = this.grade?.nativeElement;
    if (!grade) return null;

    const estilo = getComputedStyle(grade);
    const colunas = estilo.gridTemplateColumns.split(' ').map(parseFloat);
    const linhas = estilo.gridTemplateRows.split(' ').map(parseFloat);
    const gapColuna = parseFloat(estilo.columnGap) || 0;
    const gapLinha = parseFloat(estilo.rowGap) || 0;
    const r = grade.getBoundingClientRect();

    const indice = (pos: number, trilhas: number[], gap: number) => {
      if (pos < 0) return -1;
      let fim = 0;
      for (let i = 0; i < trilhas.length; i++) {
        fim += trilhas[i] + (i ? gap : 0);
        if (pos < fim + gap / 2) return i;
      }
      return -1;
    };

    const coluna = indice(x - r.left, colunas, gapColuna);
    const linha = indice(y - r.top, linhas, gapLinha);
    return coluna < 0 || linha < 0 ? null : { linha, coluna };
  }
}
